import React, { useState } from 'react';
import { ArrowRight, TrendingDown, Check, X as CloseIcon } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoanComparisonModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const { selectedCountry, symbol, formatPrice, t } = useCurrency();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loanData, setLoanData] = useState({
    car_value: '',
    loan_amount: '',
    tenure_months: 36,
    employment_type: 'salaried'
  });
  const [offers, setOffers] = useState([]);
  const [bestOffer, setBestOffer] = useState('');

  const handleChange = (e) => {
    const value = e.target.type === 'number' ? parseFloat(e.target.value) : e.target.value;
    setLoanData({ ...loanData, [e.target.name]: value });
  };

  const handleCompare = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/loans/compare`, loanData);
      setOffers(response.data.offers);
      setBestOffer(response.data.best_offer);
      setStep(2);
    } catch (error) {
      toast({
        title: t('loanComparison.errorTitle'),
        description: t('loanComparison.errorDesc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return formatPrice(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto relative my-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{t('loanComparison.title')}</h2>
            <p className="text-sm text-slate-600 mt-1">{t('loanComparison.subtitle')}</p>
          </div>
          <button
            onClick={onClose}
            className="h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
          >
            <CloseIcon className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 ? (
            <div className="max-w-2xl mx-auto space-y-6">
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-2xl p-6 border border-emerald-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('loanComparison.loanDetails')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('loanComparison.carValue')} ({symbol}) *
                    </label>
                    <input
                      type="number"
                      name="car_value"
                      value={loanData.car_value}
                      onChange={handleChange}
                      required
                      min="100000"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                      placeholder={t('loanComparison.carValuePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('loanComparison.loanAmount')} ({symbol}) *
                    </label>
                    <input
                      type="number"
                      name="loan_amount"
                      value={loanData.loan_amount}
                      onChange={handleChange}
                      required
                      min="50000"
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                      placeholder={t('loanComparison.loanAmountPlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('loanComparison.tenure')} *
                    </label>
                    <select
                      name="tenure_months"
                      value={loanData.tenure_months}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    >
                      <option value="12">{t('loanComparison.tenureOptions.12')}</option>
                      <option value="24">{t('loanComparison.tenureOptions.24')}</option>
                      <option value="36">{t('loanComparison.tenureOptions.36')}</option>
                      <option value="48">{t('loanComparison.tenureOptions.48')}</option>
                      <option value="60">{t('loanComparison.tenureOptions.60')}</option>
                      <option value="72">{t('loanComparison.tenureOptions.72')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('loanComparison.employmentType')} *
                    </label>
                    <select
                      name="employment_type"
                      value={loanData.employment_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    >
                      <option value="salaried">{t('loanComparison.employmentOptions.salaried')}</option>
                      <option value="self_employed">{t('loanComparison.employmentOptions.self_employed')}</option>
                    </select>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCompare}
                disabled={loading || !loanData.car_value || !loanData.loan_amount}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-full text-lg font-semibold"
              >
                {loading ? t('loanComparison.comparingBtn') : t('loanComparison.compareBtn')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Best Offer Badge */}
              <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-2xl p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <TrendingDown className="h-6 w-6" />
                  <span className="text-lg font-semibold">{t('loanComparison.bestOfferFound')}</span>
                </div>
                <p className="text-emerald-100 text-sm">
                  {t('loanComparison.bestOfferDesc', { lender: bestOffer })}
                </p>
              </div>

              {/* Offers Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {offers.map((offer, index) => (
                  <div
                    key={index}
                    className={`relative rounded-2xl p-6 border-2 transition-all ${offer.lender_name === bestOffer
                      ? 'border-emerald-500 bg-emerald-50 shadow-lg'
                      : 'border-slate-200 bg-white hover:border-emerald-200'
                      }`}
                  >
                    {offer.lender_name === bestOffer && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
                        {t('loanComparison.bestOfferBadge')}
                      </div>
                    )}

                    <div className="flex items-center justify-center h-16 mb-4 bg-white rounded-xl p-2">
                      <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold">
                        {offer.lender_name}
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div>
                        <div className="text-sm text-slate-600">{t('loanComparison.monthlyEmi')}</div>
                        <div className="text-2xl font-bold text-slate-900">
                          {formatCurrency(offer.monthly_emi)}
                        </div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{t('loanComparison.interestRate')}</span>
                        <span className="font-semibold text-slate-900">{offer.interest_rate}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{t('loanComparison.processingFee')}</span>
                        <span className="font-semibold text-slate-900">
                          {formatCurrency(offer.processing_fee)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">{t('loanComparison.totalInterest')}</span>
                        <span className="font-semibold text-slate-900">
                          {formatCurrency(offer.total_interest)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm pt-2 border-t border-slate-200">
                        <span className="text-slate-600">{t('loanComparison.totalPayable')}</span>
                        <span className="font-bold text-slate-900">
                          {formatCurrency(offer.total_payable)}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      {offer.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Button
                      className={`w-full rounded-full ${offer.lender_name === bestOffer
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-slate-600 hover:bg-slate-700'
                        }`}
                    >
                      {t('loanComparison.applyNow')}
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="rounded-full px-8"
                >
                  {t('loanComparison.compareAgain')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoanComparisonModal;

